/**
 * Modal accesible sobre `<dialog>` nativo: foco atrapado y `Escape` gratis, sin librería.
 * `dismissible: false` lo usan las confirmaciones sensibles — de un modal de excepción no
 * se sale por accidente.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "../../lib/cn.js";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  dismissible?: boolean;
  className?: string;
}

const SIZES = { sm: "max-w-[420px]", md: "max-w-[560px]", lg: "max-w-[860px]" } as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  dismissible = true,
  className,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    function onCancel(event: Event) {
      event.preventDefault();
      if (dismissible) onClose();
    }
    dialog.addEventListener("cancel", onCancel);
    return () => dialog.removeEventListener("cancel", onCancel);
  }, [dismissible, onClose]);

  return (
    <dialog
      ref={ref}
      aria-label={typeof title === "string" ? title : undefined}
      className={cn(
        "panel-glass m-auto w-[calc(100vw-2rem)] p-0 text-ink backdrop:bg-ink/40 backdrop:backdrop-blur-[2px]",
        "open:animate-[viva-in_.14s_ease-out]",
        SIZES[size],
        className,
      )}
      onClick={(event) => {
        if (dismissible && event.target === ref.current) onClose();
      }}
    >
      <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <h2 className="font-display text-[16px] font-semibold">{title}</h2>
          {description ? (
            <p className="mt-1 text-[13px] leading-relaxed text-dim">{description}</p>
          ) : null}
        </div>
        {dismissible ? (
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            className="-mr-1 -mt-1 rounded-[8px] p-1.5 text-mute transition-colors hover:bg-fill-3 hover:text-ink"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>

      {children ? <div className="px-5 py-4">{children}</div> : null}

      {footer ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}
